import { useEffect, useState } from "react";
import axios from "axios";
import { Fish, Waves, Wind, Gauge, Clock, ChevronDown, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function scoreColor(score, isDarkMode) {
  if (score >= 8) return isDarkMode ? "text-emerald-300" : "text-emerald-700";
  if (score >= 6) return isDarkMode ? "text-lime-300" : "text-lime-700";
  if (score >= 4) return isDarkMode ? "text-amber-300" : "text-amber-700";
  return isDarkMode ? "text-orange-300" : "text-orange-700";
}

export function FishingWidget({ lat, lon, isDarkMode }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!lat || !lon) return;
    let isMounted = true;
    axios
      .get(`${API}/weather/fishing`, { params: { lat, lon }, timeout: 14000 })
      .then((response) => { if (isMounted) setData(response.data); })
      .catch((error) => { console.error("Fishing widget error:", error.message); });
    return () => { isMounted = false; };
  }, [lat, lon]);

  const panel = isDarkMode ? "bg-white/5 border-white/10" : "bg-white/80 border-gray-200";
  const muted = isDarkMode ? "text-white/55" : "text-gray-600";

  if (!data?.success) {
    return (
      <div data-testid="fishing-widget-loading">
        <div className="flex items-center gap-2 mb-3">
          <Fish className={`w-5 h-5 ${isDarkMode ? "text-emerald-300" : "text-emerald-700"}`} />
          <h3 className={`font-semibold ${isDarkMode ? "text-white/80" : "text-gray-800"}`}>Fishing Conditions</h3>
        </div>
        <div className="animate-pulse grid grid-cols-2 gap-2">
          <div className={`h-16 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-16 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
        </div>
        <div className={`animate-pulse h-20 rounded-xl mt-3 ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
        <div className="animate-pulse grid grid-cols-3 gap-2 mt-3" data-testid="fishing-visible-windows-grid">
          <div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
        </div>
        <div className="animate-pulse grid grid-cols-2 gap-2 mt-3" data-testid="fishing-visible-species-grid">
          <div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
          <div className={`h-20 rounded-xl ${isDarkMode ? "bg-white/10" : "bg-gray-200"}`} />
        </div>
      </div>
    );
  }

  const bite = data.bite || {};
  const water = data.water || {};
  const wx = data.weather || {};
  const bestStation = water.nearest_stations?.[0];

  return (
    <div data-testid="fishing-widget" className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Fish className={`w-5 h-5 shrink-0 ${isDarkMode ? "text-emerald-300" : "text-emerald-700"}`} />
          <div className="min-w-0">
            <h3 className={`font-semibold ${isDarkMode ? "text-white/85" : "text-gray-900"}`}>Fishing Conditions</h3>
            <p className={`text-xs truncate ${muted}`} data-testid="fishing-source-label">{data.source}</p>
          </div>
        </div>
        <div className="text-right shrink-0" data-testid="fishing-bite-score">
          <p className={`text-3xl font-black leading-none ${scoreColor(bite.score, isDarkMode)}`}>{bite.score}</p>
          <p className={`text-[11px] font-semibold ${muted}`}>{bite.rating}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="fishing-water-temp">
          <div className="flex items-center gap-1.5 mb-1"><Waves className="w-4 h-4 text-cyan-400" /><span className={`text-xs ${muted}`}>Water</span></div>
          <p className="text-lg font-bold">{water.water_temp_f ? `${water.water_temp_f}°F` : "No temp"}</p>
          <p className={`text-[11px] ${muted}`}>{water.flow_status || "Flow unknown"}</p>
        </div>
        <div className={`rounded-xl border p-3 ${panel}`} data-testid="fishing-wind-pressure">
          <div className="flex items-center gap-1.5 mb-1"><Wind className="w-4 h-4 text-slate-400" /><span className={`text-xs ${muted}`}>Wind</span></div>
          <p className="text-lg font-bold">{wx.wind_speed_mph ?? "—"} mph</p>
          <p className={`text-[11px] ${muted}`}>{bite.pressure_trend} pressure</p>
        </div>
      </div>

      <div className={`rounded-xl border p-3 ${panel}`} data-testid="fishing-best-approach">
        <p className={`text-xs font-semibold mb-1 ${isDarkMode ? "text-emerald-200" : "text-emerald-800"}`}>Best approach</p>
        <p className={`text-sm leading-relaxed ${muted}`}>{bite.best_approach}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="fishing-visible-windows-grid">
        {data.prime_windows?.slice(0, 3).map((window) => (
          <div key={window.label} className={`rounded-xl border p-3 ${panel}`} data-testid={`fishing-visible-window-${window.label.toLowerCase().replaceAll(" ", "-")}`}>
            <div className="flex items-center gap-1.5 mb-1"><Clock className="w-3.5 h-3.5 text-emerald-400" /><span className="text-xs font-semibold truncate">{window.label}</span></div>
            <p className="text-xs font-bold">{window.start} - {window.end}</p>
            <p className={`text-[11px] mt-1 line-clamp-2 ${muted}`}>{window.reason}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2" data-testid="fishing-visible-species-grid">
        {data.species?.slice(0, 4).map((item) => (
          <div key={item.species} className={`rounded-xl border p-3 ${panel}`} data-testid={`fishing-visible-species-${item.species.toLowerCase()}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-sm font-semibold">{item.species}</span>
              <span className={`text-sm font-black ${scoreColor(item.score, isDarkMode)}`}>{item.score}/10</span>
            </div>
            <p className={`text-[11px] line-clamp-2 ${muted}`}>{item.pattern}</p>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={(event) => { event.stopPropagation(); setExpanded((value) => !value); }}
        className={`w-full flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${isDarkMode ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/20" : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"}`}
        data-testid="fishing-widget-expand-button"
        aria-expanded={expanded}
      >
        {expanded ? "Hide fishing details" : "Show fishing details"}
        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="space-y-3" data-testid="fishing-widget-expanded-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {data.prime_windows?.map((window) => (
              <div key={window.label} className={`rounded-xl border p-3 ${panel}`} data-testid={`fishing-window-${window.label.toLowerCase().replaceAll(" ", "-")}`}>
                <div className="flex items-center gap-1.5 mb-1"><Clock className="w-3.5 h-3.5 text-emerald-400" /><span className="text-xs font-semibold">{window.label}</span></div>
                <p className="text-xs font-bold">{window.start} - {window.end}</p>
                <p className={`text-[11px] mt-1 ${muted}`}>{window.reason}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {data.species?.map((item) => (
              <div key={item.species} className={`rounded-xl border p-3 ${panel}`} data-testid={`fishing-species-${item.species.toLowerCase()}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-sm font-semibold">{item.species}</span>
                  <span className={`text-sm font-black ${scoreColor(item.score, isDarkMode)}`}>{item.score}/10</span>
                </div>
                <p className={`text-[11px] ${muted}`}>{item.pattern}</p>
              </div>
            ))}
          </div>
          {bestStation && (
            <div className={`rounded-xl border p-3 ${panel}`} data-testid="fishing-nearest-usgs-station">
              <div className="flex items-center gap-1.5 mb-1"><Gauge className="w-4 h-4 text-blue-400" /><span className="text-xs font-semibold">Nearest USGS station</span></div>
              <p className="text-sm font-bold truncate">{bestStation.name}</p>
              <p className={`text-xs ${muted}`}>{bestStation.distance_mi} mi · {bestStation.discharge_cfs ? `${bestStation.discharge_cfs} cfs` : "flow not reported"} · {bestStation.wadeability}</p>
            </div>
          )}
          {bite.cautions?.length > 0 && (
            <div className={`rounded-xl border p-3 ${isDarkMode ? "bg-amber-500/10 border-amber-400/20" : "bg-amber-50 border-amber-200"}`} data-testid="fishing-cautions-panel">
              <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-xs font-semibold">Cautions</span></div>
              {bite.cautions.map((caution) => <p key={caution} className={`text-xs ${muted}`}>{caution}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}